export interface SparkRenderSettingsConfig {
  blurAmount: number;
  preBlurAmount: number;
  maxStdDev: number;
  pixelRatio: number;
  minPixelRadius: number;
  maxPixelRadius: number;
  autoUpdate: boolean;
  sortRadial: boolean;
  sort32: boolean;
  sortDistance: number;
  sortCoorient: boolean;
  depthBias: number;
  sort360: boolean;
  stochastic: boolean;
  premultipliedAlpha: boolean;
  preUpdate: boolean;
  originDistance: number;
  minAlpha: number;
  enable2DGS: boolean;
  focalDistance: number;
  apertureAngle: number;
  falloff: number;
  clipXY: number;
  focalAdjustment: number;
}

export type DeviceClass = 'desktop' | 'mobile';
export type DeviceTier = 'low' | 'middle' | 'high';

export interface QualityProfile {
  name: 'mobile' | 'desktop';
  deviceClass: DeviceClass;
  tier: DeviceTier;
  renderSettings: SparkRenderSettingsConfig;
  cameraPositionThreshold: number;
  cameraRotationThreshold: number;
  benchmarkMs?: number;
  benchmarkCv?: number;
}

const BASE_RENDER_SETTINGS: SparkRenderSettingsConfig = {
  blurAmount: 0,
  preBlurAmount: 0.1,
  maxStdDev: Math.sqrt(8),
  pixelRatio: 2,
  minPixelRadius: 0,
  maxPixelRadius: 512,
  autoUpdate: false,
  sortRadial: true,
  sort32: false,
  sortDistance: 0.01,
  sortCoorient: true,
  depthBias: 1.0,
  sort360: false,
  stochastic: false,
  premultipliedAlpha: true,
  preUpdate: false,
  originDistance: 1.0,
  minAlpha: 0.5 * (1 / 255),
  enable2DGS: false,
  focalDistance: 0,
  apertureAngle: 0,
  falloff: 1.0,
  clipXY: 1.4,
  focalAdjustment: 1.0,
};

const DESKTOP_RENDER_SETTINGS: SparkRenderSettingsConfig = {
  ...BASE_RENDER_SETTINGS,
};

const DESKTOP_CAMERA_POSITION_THRESHOLD = 0.0005;
const DESKTOP_CAMERA_ROTATION_THRESHOLD = 0.0002;

// --- Desktop profiles (all tiers identical — nominal for type unification) ---

export const DESKTOP_LOW_QUALITY_PROFILE: QualityProfile = {
  name: 'desktop',
  deviceClass: 'desktop',
  tier: 'low',
  renderSettings: { ...DESKTOP_RENDER_SETTINGS },
  cameraPositionThreshold: DESKTOP_CAMERA_POSITION_THRESHOLD,
  cameraRotationThreshold: DESKTOP_CAMERA_ROTATION_THRESHOLD,
};

export const DESKTOP_MIDDLE_QUALITY_PROFILE: QualityProfile = {
  name: 'desktop',
  deviceClass: 'desktop',
  tier: 'middle',
  renderSettings: { ...DESKTOP_RENDER_SETTINGS },
  cameraPositionThreshold: DESKTOP_CAMERA_POSITION_THRESHOLD,
  cameraRotationThreshold: DESKTOP_CAMERA_ROTATION_THRESHOLD,
};

export const DESKTOP_HIGH_QUALITY_PROFILE: QualityProfile = {
  name: 'desktop',
  deviceClass: 'desktop',
  tier: 'high',
  renderSettings: { ...DESKTOP_RENDER_SETTINGS },
  cameraPositionThreshold: DESKTOP_CAMERA_POSITION_THRESHOLD,
  cameraRotationThreshold: DESKTOP_CAMERA_ROTATION_THRESHOLD,
};

/** @deprecated Use DESKTOP_HIGH_QUALITY_PROFILE */
export const DESKTOP_QUALITY_PROFILE = DESKTOP_HIGH_QUALITY_PROFILE;

// --- Mobile profiles ---

export const MOBILE_HIGH_QUALITY_PROFILE: QualityProfile = {
  name: 'mobile',
  deviceClass: 'mobile',
  tier: 'high',
  renderSettings: {
    ...BASE_RENDER_SETTINGS,
    pixelRatio: 1.6,
    maxStdDev: Math.sqrt(4),
    minPixelRadius: 0,
    maxPixelRadius: 192,
    minAlpha: 0.02,
    originDistance: 0.4,
  },
  cameraPositionThreshold: 0.001,
  cameraRotationThreshold: 0.0005,
};

export const MOBILE_MIDDLE_QUALITY_PROFILE: QualityProfile = {
  name: 'mobile',
  deviceClass: 'mobile',
  tier: 'middle',
  renderSettings: {
    ...BASE_RENDER_SETTINGS,
    pixelRatio: 1.5,
    maxStdDev: Math.sqrt(3),
    minPixelRadius: 0,
    maxPixelRadius: 128,
    minAlpha: 0.025,
    originDistance: 0.5,
  },
  cameraPositionThreshold: 0.001,
  cameraRotationThreshold: 0.0005,
};

export const MOBILE_LOW_QUALITY_PROFILE: QualityProfile = {
  name: 'mobile',
  deviceClass: 'mobile',
  tier: 'low',
  renderSettings: {
    ...BASE_RENDER_SETTINGS,
    pixelRatio: 1.2,
    maxStdDev: Math.sqrt(2.5),
    minPixelRadius: 0,
    maxPixelRadius: 64,
    minAlpha: 0.03,
    originDistance: 0.6,
  },
  cameraPositionThreshold: 0.001,
  cameraRotationThreshold: 0.0005,
};

/** @deprecated Use MOBILE_HIGH_QUALITY_PROFILE */
export const MOBILE_QUALITY_PROFILE = MOBILE_HIGH_QUALITY_PROFILE;

// --- Device detection ---

type NavigatorWithExtensions = Navigator & {
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    saveData?: boolean;
  };
};

const detectIsMobile = (): boolean => {
  const nav = navigator as NavigatorWithExtensions;
  const ua = nav.userAgent ?? '';
  const isTouchDevice = 'ontouchstart' in window || nav.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 900;
  const lowMemory =
    typeof nav.deviceMemory === 'number' &&
    nav.deviceMemory > 0 &&
    nav.deviceMemory <= 4;
  const connection = nav.connection;
  const saveData = Boolean(connection?.saveData);
  const slowConnection =
    typeof connection?.effectiveType === 'string'
      ? /2g|3g/.test(connection.effectiveType.toLowerCase())
      : false;
  const isMobileUA = /Android|iPhone|iPad|iPod|Windows Phone|Mobi/i.test(ua);

  return (
    isMobileUA ||
    (isTouchDevice && isSmallScreen) ||
    lowMemory ||
    saveData ||
    slowConnection
  );
};

// --- GPU renderer detection ---

const getGpuRenderer = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ?? canvas.getContext('webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return '';
    return (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string) ?? '';
  } catch {
    return '';
  }
};

const scoreGpuRenderer = (renderer: string): number => {
  if (!renderer) return 10;
  if (/SwiftShader|llvmpipe/i.test(renderer)) return 0;
  if (/Apple\s*M\d/i.test(renderer)) return 50;
  if (/Adreno\s*[7-9]\d{2}/i.test(renderer)) return 35;
  if (/Apple\s*GPU/i.test(renderer)) {
    return window.devicePixelRatio >= 3 ? 50 : 30;
  }
  if (/Adreno\s*6\d{2}|Mali-G7/i.test(renderer)) return 20;
  if (/Mali|Adreno\s*[2-5]\d{2}/i.test(renderer)) return 5;
  return 10;
};

// --- Mobile scoring ---

const computeMobileScore = (): number => {
  const nav = navigator as NavigatorWithExtensions;
  let score = 0;

  // CPU cores (reduced weight — less relevant for 3DGS)
  const cores = nav.hardwareConcurrency ?? 0;
  if (cores >= 6) score += 10;
  else if (cores >= 4) score += 5;

  // Device memory (reduced weight)
  const memory = nav.deviceMemory;
  if (typeof memory === 'number' && memory > 0) {
    if (memory >= 6) score += 10;
    else if (memory >= 4) score += 5;
  } else {
    score += 5; // unknown — below neutral
  }

  // GPU
  const gpuRenderer = getGpuRenderer();
  score += scoreGpuRenderer(gpuRenderer);

  // WebGPU (reduced bonus)
  if ('gpu' in navigator) score += 5;

  // Network penalties
  const connection = nav.connection;
  if (connection?.saveData) score -= 10;
  if (
    typeof connection?.effectiveType === 'string' &&
    /2g|3g/.test(connection.effectiveType.toLowerCase())
  ) {
    score -= 10;
  }

  return score;
};

const classifyMobileTier = (score: number): DeviceTier => {
  if (score >= 55) return 'high';
  if (score >= 35) return 'middle';
  return 'low';
};

// --- Main detection ---

const MOBILE_PROFILES: Record<DeviceTier, QualityProfile> = {
  low: MOBILE_LOW_QUALITY_PROFILE,
  middle: MOBILE_MIDDLE_QUALITY_PROFILE,
  high: MOBILE_HIGH_QUALITY_PROFILE,
};

export function detectQualityProfile(): QualityProfile {
  if (!detectIsMobile()) return DESKTOP_HIGH_QUALITY_PROFILE;

  const score = computeMobileScore();
  const tier = classifyMobileTier(score);

  return MOBILE_PROFILES[tier];
}

// --- Benchmark-based detection (async) ---

const LOG_PREFIX = '[gpu-benchmark]';

const classifyBenchmarkTier = (medianMs: number): DeviceTier => {
  if (medianMs <= 8) return 'high';
  if (medianMs <= 12) return 'middle';
  return 'low';
};

/**
 * Cached profile promise — quality detection runs at most ONCE per session.
 *
 * **iOS OOM fix (critical):** the GPU benchmark inside `runGpuBenchmark`
 * creates a fresh `<canvas>` + WebGL2 context and ends with
 * `WEBGL_lose_context.loseContext()`. On iOS Safari, lost-context memory is
 * reclaimed asynchronously (multi-second delay via the memory-pressure
 * handler). Calling `detectQualityProfileAsync` from `SceneCanvas.useEffect`
 * on every splat-scene mount accumulated one pending lost-context per
 * transition — the same pattern that prompted the shared-renderer refactor
 * (see docs/architecture/webgl-memory.md). After 2–3 transitions
 * iOS hit its per-tab WebGL context limit and OOM-killed the tab.
 *
 * Caching at the module level guarantees the benchmark runs at most once for
 * the whole tab lifetime. GPU performance does not change between scene
 * transitions, so the result is reusable. Concurrent callers awaiting the
 * same promise share its result without spawning additional contexts.
 */
let cachedProfilePromise: Promise<QualityProfile> | null = null;

const computeQualityProfile = async (): Promise<QualityProfile> => {
  const forcedTier = new URLSearchParams(window.location.search).get('force-tier') as DeviceTier | null;
  if (forcedTier && forcedTier in MOBILE_PROFILES) {
    console.log(LOG_PREFIX, `forced tier=${forcedTier} via URL param`);
    return MOBILE_PROFILES[forcedTier];
  }

  if (!detectIsMobile()) return DESKTOP_HIGH_QUALITY_PROFILE;

  try {
    const { runGpuBenchmark } = await import('./gpuBenchmark');
    const { medianMs, cv } = await runGpuBenchmark();
    const tier = classifyBenchmarkTier(medianMs);

    console.log(LOG_PREFIX, `tier=${tier} (median=${medianMs.toFixed(2)}ms, cv=${cv.toFixed(2)})`);

    return { ...MOBILE_PROFILES[tier], benchmarkMs: medianMs, benchmarkCv: cv };
  } catch (error) {
    console.warn(LOG_PREFIX, 'failed, using fallback heuristic:', error);
    return detectQualityProfile();
  }
};

export async function detectQualityProfileAsync(): Promise<QualityProfile> {
  if (cachedProfilePromise) return cachedProfilePromise;

  // Cache the in-flight promise itself (not just the resolved value) so
  // concurrent callers — e.g. rapid scene transitions — await the same
  // benchmark run instead of starting their own.
  cachedProfilePromise = computeQualityProfile().catch((error) => {
    // On unexpected failure, drop the cache so the next caller can retry.
    // `computeQualityProfile` already swallows benchmark failures internally
    // (returns the heuristic fallback), so this branch only fires for truly
    // unexpected errors (import failure, etc.).
    cachedProfilePromise = null;
    throw error;
  });

  return cachedProfilePromise;
}
